import {
  BroadSoftSkill,
  Orientation,
  SoftSkillCategory,
  SoftSkillFindManyPaginatedQuery,
  SoftSkillFragment,
  SoftSkillGranularity,
  SoftSkillSortField,
} from 'generated/graphql';
import styles from 'views/StaticContent/SoftSkills/SoftSkills.module.scss';
import { GridCellParams, GridValidRowModel } from '@spotted-zebra-uk/ui';
import { Button, TSelectOption } from '@spotted-zebra-uk/ui-components';

export enum QueryParams {
  SEARCH = 'search',
  PAGE = 'page',
  PAGE_SIZE = 'pageSize',
  WITH_DELETED = 'displayArchived',
  COMPANY_ID = 'companyId',
  EDIT_DRAWER = 'softSkill',
  ADD_DRAWER = 'addSoftSkill',
  EDIT_TAB = 'editTab',
}

export enum SoftSkillDrawerTab {
  TRAITS = 'traits',
  RELATIONS = 'relations',
  SIBLINGS = 'siblings',
  EXAMPLES = 'examples',
  CONTENT = 'content',
}

export const getTabFromIndex = (index: number): SoftSkillDrawerTab => {
  return Object.values(SoftSkillDrawerTab)[index];
};

export const getIndexFromTab = (tab: SoftSkillDrawerTab): number => {
  return Object.values(SoftSkillDrawerTab).indexOf(tab) ?? 0;
};

const DESCRIPTION_PLACEHOLDER = 'Missing description';
const TAXONOMY_VERSION_PLACEHOLDER = '1';
const OWNER_PLACEHOLDER = 'Global';
const UNEDITABLE_SKILLS: readonly string[] = [
  'cognitive inductive',
  'cognitive numerical',
  'cognitive verbal',
  'cognitive combined',
  'right for us',
  'right for the role',
  'right for the future',
  'cognitive summary',
  'numerical reasoning',
  'verbal reasoning',
  'inductive reasoning',
] as const;

export type TRequiredSelectOption<T> = Required<
  Pick<TSelectOption<T>, 'label' | 'value'>
>;

type TSoftSkillFragmentReturnType = NonNullable<
  SoftSkillFindManyPaginatedQuery['SoftSkillFindManyPaginated']['data']
>[number];

export const isBroad = (
  softSkill: SoftSkillFragment | undefined
): softSkill is BroadSoftSkill & { __typename: 'BroadSoftSkill' } => {
  return softSkill?.__typename === 'BroadSoftSkill';
};

export const formatGranularity = (
  granularity: SoftSkillGranularity | undefined
): string => {
  switch (granularity) {
    case SoftSkillGranularity.Broad:
      return 'Broad';
    case SoftSkillGranularity.Focused:
    default:
      return 'Focused';
  }
};

export const formatCategory = (
  category: SoftSkillCategory | undefined
): string => {
  switch (category) {
    case SoftSkillCategory.RightForUs:
      return 'Right for us';
    case SoftSkillCategory.RightForTheFuture:
      return 'Right for the future';
    case SoftSkillCategory.CognitiveSummary:
      return 'Cognitive summary';
    default:
    case SoftSkillCategory.RightForTheRole:
      return 'Right for the role';
  }
};

export const formatOrientation = (orientation: Orientation): string => {
  switch (orientation) {
    case Orientation.Inverted:
      return 'Inverted';
    default:
    case Orientation.Standard:
      return 'Standard';
  }
};

export const DEFAULT_TABLE_PAGE_SIZE = 20;

export enum SoftSkillTableColumns {
  Name = 'softSkillName',
  Description = 'softSkillDescription',
  TaxonomyVersion = 'softSkillTaxonomyVersion',
  Granularity = 'softSkillGranularity',
  Category = 'softSkillCategory',
  Ownership = 'ownership',
  Actions = 'edit',
}

export const getTableSkillSortField = (
  tableColumn: SoftSkillTableColumns
): SoftSkillSortField | null => {
  switch (tableColumn) {
    case SoftSkillTableColumns.Name:
      return SoftSkillSortField.Name;
    case SoftSkillTableColumns.TaxonomyVersion:
      return SoftSkillSortField.Taxonomy;
    case SoftSkillTableColumns.Granularity:
      return SoftSkillSortField.Granularity;
    case SoftSkillTableColumns.Category:
      return SoftSkillSortField.Category;
    default:
      return null;
  }
};

export const COLUMNS = [
  {
    field: SoftSkillTableColumns.Name,
    headerName: 'Name',
    width: 150,
    flex: 0.4,
    sortable: true,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { name: string; deletedAt: string }
    >) => {
      if (value?.deletedAt) {
        return <span className={styles.disabledCell}>{value.name}</span>;
      }
      return <span>{value?.name}</span>;
    },
  },
  {
    field: SoftSkillTableColumns.Description,
    headerName: 'Description',
    width: 200,
    flex: 0.8,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { description: string; deletedAt: string }
    >) => {
      if (value?.deletedAt || !value?.description) {
        return (
          <span className={styles.disabledCell}>
            {value?.description || DESCRIPTION_PLACEHOLDER}
          </span>
        );
      }
      return <span>{String(value.description)}</span>;
    },
  },
  {
    field: SoftSkillTableColumns.TaxonomyVersion,
    headerName: 'Taxonomy',
    width: 100,
    flex: 0.2,
    sortable: true,
    renderCell: ({
      value,
    }: GridCellParams<GridValidRowModel, TSoftSkillFragmentReturnType>) => {
      if (value?.deletedAt) {
        return (
          <span className={styles.disabledCell}>
            {`v${value?.taxonomyVersion ?? TAXONOMY_VERSION_PLACEHOLDER}`}
          </span>
        );
      }
      return <span>{`v${value?.taxonomyVersion}`}</span>;
    },
  },
  {
    field: SoftSkillTableColumns.Granularity,
    headerName: 'Granularity',
    width: 150,
    flex: 0.3,
    sortable: true,
    renderCell: ({
      value,
    }: GridCellParams<GridValidRowModel, TSoftSkillFragmentReturnType>) => {
      return (
        <span className={value?.deletedAt ? styles.disabledCell : ''}>
          {formatGranularity(
            isBroad(value)
              ? SoftSkillGranularity.Broad
              : SoftSkillGranularity.Focused
          )}
        </span>
      );
    },
  },
  {
    field: SoftSkillTableColumns.Category,
    headerName: 'Category',
    width: 150,
    flex: 0.3,
    sortable: true,
    renderCell: ({
      value,
    }: GridCellParams<GridValidRowModel, TSoftSkillFragmentReturnType>) => {
      if (value?.deletedAt || !value?.category) {
        return (
          <span className={styles.disabledCell}>
            {formatCategory(value?.category)}
          </span>
        );
      }
      return <span>{formatCategory(value.category)}</span>;
    },
  },
  {
    field: SoftSkillTableColumns.Ownership,
    headerName: 'Ownership',
    width: 100,
    flex: 0.3,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      { companyName: string; deletedAt: string }
    >) => {
      if (value?.deletedAt || !value?.companyName) {
        return (
          <span className={styles.disabledCell}>
            {value?.companyName ?? OWNER_PLACEHOLDER}
          </span>
        );
      }
      return <span>{String(value.companyName)}</span>;
    },
  },
  {
    field: SoftSkillTableColumns.Actions,
    headerName: '',
    flex: 0.1,
    width: 100,
    sortable: false,
    renderCell: ({
      value,
    }: GridCellParams<
      GridValidRowModel,
      {
        softSkill: TSoftSkillFragmentReturnType;
        editCallback: (softSkill: TSoftSkillFragmentReturnType) => undefined;
      }
    >) => {
      if (
        UNEDITABLE_SKILLS.includes(
          value?.softSkill.name?.toLowerCase() as string
        )
      ) {
        return '';
      }
      return (
        <div className={styles.editButtonContainer}>
          <Button
            size="small"
            variant="secondary"
            onClick={() => value?.editCallback(value.softSkill)}
          >
            Edit
          </Button>
        </div>
      );
    },
  },
];

export const parseRow = (
  softSkill: TSoftSkillFragmentReturnType,
  editCallback: (softSkill: TSoftSkillFragmentReturnType) => void
) => {
  return {
    id: softSkill.id,
    _id: { id: softSkill.id, deletedAt: softSkill.deletedAt },
    subId: { subId: softSkill.subId, deletedAt: softSkill.deletedAt },
    softSkillName: { name: softSkill.name, deletedAt: softSkill.deletedAt },
    softSkillDescription: {
      description: softSkill.description,
      deletedAt: softSkill.deletedAt,
    },
    softSkillTaxonomyVersion: softSkill,
    softSkillGranularity: softSkill,
    ownership: {
      companyName: softSkill?.company?.name,
      deletedAt: softSkill.deletedAt,
    },
    softSkillCategory: softSkill,
    edit: { softSkill, editCallback },
    _softSkill: softSkill,
  };
};
